<template>
  <FlugstreckenVergleichLayout
    :ist-title="computedIstTitle"
    :soll-title="computedSollTitle"
    :status-pruefung="statusPruefungAggregated"
    :status-tickets="statusTicketsAggregated"
  >
    <!-- Ist Content -->
    <template #ist-content>
      <div
        v-for="inlandsflug in inlandsfluegeIst"
        :key="'inlandsflugIst' + inlandsflug.id"
        class="mt-7"
        style=""
      >
        <InlandsflugSegmentAnzeige :flug="inlandsflug" />
      </div>
    </template>

    <!-- Soll Content -->
    <template v-if="showSollContent" #soll-content>
      <div
        v-for="(inlandsflug, index) in inlandsfluegeSoll"
        :key="'inlandsflugSoll' + (inlandsflug.id || `spacer-${index}`)"
        class="mt-7"
      >
        <!-- Spacer für zusätzliche Ist-Segmente -->
        <div v-if="inlandsflug.isSpacer" :style="inlandsflug.style"></div>

        <!-- Normaler Soll-Content -->
        <div
          v-else
          class="d-flex align-items-center"
          :style="inlandsflug.hasBorder ? 'padding: 4px 0px' : ''"
        >
          <!-- Abflughafen - Seite -->
          <div class="d-flex flex-column" style="width: 140px">
            <div class="mb-2 grey500" style="text-align: center">
              {{ inlandsflug.abflughafen }}
            </div>

            <div class="d-flex justify-content-center grey400" style="gap: 3px; align-items: center">
              {{ formatDate(inlandsflug.fruehesterAbflug) }}
            </div>
          </div>

          <div style="flex-grow: 1; display: flex; flex-direction: column; align-items: center">
            <div style="height: 21px"></div>
            <div class="horizontal-line w-100"></div>
            <div class="mt-4">{{ inlandsflug.flugnummer ?? '---' }}</div>
          </div>

          <!-- Zielflughafen - Seite  -->
          <div class="d-flex flex-column" style="width: 140px">
            <div class="mb-2 grey500" style="text-align: center">
              {{ inlandsflug.zielflughafen }}
            </div>
            <div class="d-flex justify-content-center grey400" style="gap: 3px; align-items: center">
              <div v-if="inlandsflug.spaetesteAnkunft != null">
                {{ formatDate(inlandsflug.spaetesteAnkunft) }}
              </div>
              <div v-else-if="inlandsflug.minimaleUmsteigezeit != null">
                {{ ` Min. Umsteigezeit: ${inlandsflug.minimaleUmsteigezeit}h` }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </FlugstreckenVergleichLayout>
</template>

<script>
import { isBefore, format, subMinutes, addMinutes } from 'date-fns';
import FlugstreckenVergleichLayout from './flugstrecken-vergleich-layout.vue';
import InlandsflugSegmentAnzeige from './inlandsflug-segment-anzeige.vue';

export default {
  components: { InlandsflugSegmentAnzeige, FlugstreckenVergleichLayout },
  props: {
    flugstrecken: { type: Array },
    showSollContent: { type: Boolean, default: true },
    istTitle: { type: String, default: null },
    sollTitle: { type: String, default: null },
  },
  data() {
    return {};
  },
  computed: {
    computedIstTitle() {
      return this.istTitle || 'Inlandsflüge Ist';
    },
    computedSollTitle() {
      return this.sollTitle || 'Inlandsflüge Soll';
    },
    inlandsfluegeSoll() {
      const result = [];

      this.flugstrecken.forEach((flugstrecke, index) => {
        const segmentCount = flugstrecke.segmente?.length || 1;

        // Füge Spacer für alle Segmente außer dem letzten hinzu
        for (let i = 0; i < segmentCount - 1; i++) {
          result.push({
            isSpacer: true,
            style: 'min-height: 35px;',
          });
        }

        result.push({
          ...flugstrecke.reiseterminFlugstrecke,
          countSegmente: flugstrecke.segmente.length,
          isSpacer: false,
          hasBorder: this.addPaddingIfIstContentHasBorder(index),
        });
      });

      return result;
    },
    inlandsfluegeIst() {
      return this.flugstrecken.reduce((accumulator, flugstrecke) => {
        const inlandsflugStrecke = {
          id: flugstrecke.reiseterminFlugstrecke.id,
          segmente: flugstrecke.segmente.map(segment => ({
            ...segment,
            flugnummerNotMatching: !(
              (flugstrecke.segmente.length == 1 || flugstrecke.segmente.length == 2) &&
              (flugstrecke.reiseterminFlugstrecke.flugnummer?.includes(
                `${segment.betreiber}${segment.flugnummer}`
              ) ||
                flugstrecke.reiseterminFlugstrecke.flugnummer == null)
            ),
            restriktionenAbflugOutOfWindow: this.restriktionenAbflugOutOfWindow(flugstrecke),
            restriktionenAnkunftOutOfWindow: this.restriktionenAnkunftOutOfWindow(flugstrecke),
          })),
          einkaeufer: flugstrecke.reiseterminFlugstrecke.inlandsflugEinkaeufer,
          restriktionenHinRelevant:
            flugstrecke.reiseterminFlugstrecke.inlandsflugEinkaeufer === 'Dmc' ||
            (flugstrecke.segmente.length !== 1 && flugstrecke.segmente.length !== 2) ||
            flugstrecke.reiseterminFlugstrecke.minimaleUmsteigezeit != null
              ? false
              : true,
          restriktionenRueckRelevant:
            flugstrecke.reiseterminFlugstrecke.inlandsflugEinkaeufer === 'Dmc' ||
            (flugstrecke.segmente.length !== 1 && flugstrecke.segmente.length !== 2)
              ? false
              : true,
        };
        accumulator.push(inlandsflugStrecke);
        return accumulator;
      }, []);
    },
    statusPruefungAggregated() {
      return this.flugstrecken.some(f => f.statusPruefung === 'NoSegment')
        ? 'NoSegment'
        : this.flugstrecken.some(f => f.statusPruefung === 'SomethingWrong')
        ? 'SomethingWrong'
        : 'AllGood';
    },
    statusTicketsAggregated() {
      return this.flugstrecken.some(f => f.statusTickets === 'Critical')
        ? 'Critical'
        : this.flugstrecken.some(f => f.statusTickets === 'CheckRequired')
        ? 'CheckRequired'
        : 'AllGood';
    },
  },
  methods: {
    addPaddingIfIstContentHasBorder(index) {
      if (!this.flugstrecken || !this.flugstrecken[index]) return false;

      const flugstrecke = this.flugstrecken[index];
      if (!flugstrecke.segmente || flugstrecke.segmente.length === 0) return false;

      return flugstrecke.segmente.some(
        segment => segment?.segmentStatus === 'UN' || segment?.segmentStatus === 'TK'
      );
    },
    restriktionenAbflugOutOfWindow(flugstrecke) {
      const { fruehesterAbflug, spaetesteAnkunft, minimaleUmsteigezeit } = flugstrecke.reiseterminFlugstrecke;

      if (flugstrecke.segmente.length == 0 || flugstrecke.segmente.length > 1 || minimaleUmsteigezeit != null)
        return false;
      if (fruehesterAbflug == null || spaetesteAnkunft == null) return false;

      const { abflugsdatum } = flugstrecke.segmente[0];

      return (
        isBefore(new Date(abflugsdatum), subMinutes(new Date(fruehesterAbflug), 15)) ||
        isBefore(addMinutes(new Date(spaetesteAnkunft), 15), new Date(abflugsdatum))
      );
    },
    restriktionenAnkunftOutOfWindow(flugstrecke) {
      const { fruehesterAbflug, spaetesteAnkunft, minimaleUmsteigezeit } = flugstrecke.reiseterminFlugstrecke;

      if (minimaleUmsteigezeit != null) {
        return flugstrecke.statusPruefung !== 'AllGood';
      }

      if (flugstrecke.segmente.length == 0 || flugstrecke.segmente.length > 1) return false;
      if (fruehesterAbflug == null || spaetesteAnkunft == null) return false;

      const { ankunftsdatum } = flugstrecke.segmente[0];

      return (
        isBefore(new Date(ankunftsdatum), subMinutes(new Date(fruehesterAbflug), 15)) ||
        isBefore(addMinutes(new Date(spaetesteAnkunft), 15), new Date(ankunftsdatum))
      );
    },
    formatDate(date) {
      if (!date) return '-';

      return format(new Date(date.slice(0, -1)), 'dd.MM.yy HH:mm');
    },
  },
};
</script>

<style scoped>
.horizontal-line {
  border-top: 1px dashed #c5ced6;
  height: 0;
  flex-grow: 1;
  margin-left: 8px;
  margin-right: 8px;
}

.grey400 {
  font-weight: 400;
  color: var(--text-body);
}

.grey500 {
  font-weight: 500;
  color: var(--text-body);
  font-size: 20px;
}
</style>
